import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import SweetAlert from "react-bootstrap-sweetalert";
import { URLS } from "src/_config";
import { useUsersManagementApi } from "src/_common/hooks/actions";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import InviteMemberModal from "./InviteMemberModal";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";
import { hasPermission } from "src/_common/utils/permissionsUtils";

interface Member {
  user_id: number;
  id: string;
  name: string;
  email: string;
  assign_workspace: string;
  created_at: {
    $date: {
      $numberLong: string;
    };
  };
  role_id: number;
  updated_at: string;
  employee_code: string;
  designation: string;
  profile_photo_url?: string;
  status: boolean;
}

interface PaginationInfo {
  total_members: number;
  current_page: number;
  total_pages: number;
  limit: number;
  offset: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

interface ApiResponse {
  members: Member[];
  pagination: PaginationInfo;
  user_info: {
    accessible_member_count: number;
    current_user_id: number;
  };
}

const MemberList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [totalMembers, setTotalMembers] = useState(0);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; member: Member | null }>({
    show: false,
    member: null,
  });
  const [isDeletingMember, setIsDeletingMember] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [resendModal, setResendModal] = useState<{ show: boolean; member: Member | null }>({
    show: false,
    member: null,
  });
  const [isResendingEmail, setIsResendingEmail] = useState(false);

  // Initialize API hooks and translation
  const { callGetUsers, callDeleteUser, callResendWelcomeEmail } = useUsersManagementApi();
  const toast = useToaster();
  const history = useHistory();
  const { t } = useTranslation("member");
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();
  // Constants for pagination
  const ITEMS_PER_PAGE = 10;

  // Function to format date
  const formatDate = (dateObj: any) => {
    if (!dateObj || !dateObj.$date || !dateObj.$date.$numberLong) return "--";
    const timestamp = parseInt(dateObj.$date.$numberLong);
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Function to format updated date
  const formatUpdatedDate = (dateString: string) => {
    if (!dateString) return "--";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };


  // Function to load users with pagination
  const loadUsers = (page: number = 1, search: string = "") => {
    setLoading(true);
    const offset = (page - 1) * ITEMS_PER_PAGE;
    const params = {
      limit: ITEMS_PER_PAGE,
      offset: offset,
      ...{ search: search },
    };

    callGetUsers(
      params,
      (message: string, data: ApiResponse) => {
        console.log("Users loaded successfully:", message);
        if (data && data.members) {
          setMembers(data.members);
          setPagination(data.pagination);
          setTotalMembers(data.pagination.total_members);
        }
        setLoading(false);
      },
      (error: string) => {
        console.error("Error loading users:", error);
        setLoading(false);
      }
    );
  };

  // Load users on component mount
  useEffect(() => {
    loadUsers(currentPage, searchTerm);
  }, [currentPage]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    loadUsers(1, searchTerm);
  };

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === 'member') {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Check permissions for different actions
  const canReadMember = canAccessMenu('read_members');
  const canCreateMember = canAccessMenu('create_members');
  const canEditMember = canAccessMenu('edit_members');
  const canDeleteMember = canAccessMenu('delete_members');

  // If member doesn't have read permission, don't render the component
  if (userDetails?.user_type === 'member' && !canReadMember) {
    return null;
  }

  // Handle delete member
  const handleDeleteClick = (member: Member) => {
    setDeleteModal({ show: true, member });
  };

  const handleDeleteConfirm = () => {
    if (!deleteModal.member) return;

    const userId = deleteModal.member.user_id;
    setIsDeletingMember(true);

    callDeleteUser(
      userId,
      (message: string, data: any) => {
        toast.success(message || t("members.messages.deleteSuccess"));
        setDeleteModal({ show: false, member: null });
        setIsDeletingMember(false);
        // Reload the current page
        loadUsers(currentPage, searchTerm);
      },
      (error: string) => {
        console.error("Error deleting member:", error);
        toast.error(error || t("members.messages.deleteError"));
        setIsDeletingMember(false);
      }
    );
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, member: null });
  };

  // Handle invite member
  const handleInviteClick = () => {
    setInviteModalOpen(true);
  };

  const handleInviteClose = () => {
    setInviteModalOpen(false);
  };

  const handleInviteSuccess = () => {
    // Reload the current page after successful invite
    // loadUsers(currentPage, searchTerm);
    setInviteModalOpen(false);
  };

  // Handle resend welcome email
  const handleResendWelcomeEmailClick = (member: Member) => {
    setResendModal({ show: true, member });
  };

  const handleResendWelcomeEmailConfirm = () => {
    if (!resendModal.member) return;

    const userId = resendModal.member.user_id;
    setIsResendingEmail(true);

    callResendWelcomeEmail(
      userId,
      (message: string, data: any) => {
        toast.success(message || t("members.messages.emailSuccess"));
        setResendModal({ show: false, member: null });
        setIsResendingEmail(false);
      },
      (error: string) => {
        console.error("Error resending welcome email:", error);
        toast.error(error || t("members.messages.emailError"));
        setIsResendingEmail(false);
      }
    );
  };

  const handleResendWelcomeEmailCancel = () => {
    setResendModal({ show: false, member: null });
  };

  // Filter members based on search term (client-side filtering for current page)
  const filteredMembers = members.filter(
    (member) =>
      (member.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.assign_workspace || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.designation || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate pagination display
  const totalPages = Math.ceil(totalMembers / ITEMS_PER_PAGE);
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, currentPage + 2);
  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="dashboard-main member-page-wrapper">
      {/* Header Section */}
      <div className="drjob-page-header">
        <div className="drjob-header-left">
          <button className="back-button d-none" onClick={() => history.push(URLS.DASHBOARD.MAIN)}>
            {t("members.actions.back")}
          </button>
          <h1 className="drjob-page-title">{t("members.title")}</h1>
        </div>
        <div className="drjob-header-right">
          <button onClick={handleInviteClick} className="btn-cancel">
            {t("members.actions.inviteMember")}
          </button>
          {canCreateMember && (
            <button onClick={() => history.push(URLS.MEMBERS.ADD)} className="btn-preview">
              {t("members.actions.addMember")}
            </button>
          )}
        </div>
      </div>

      {/* Search Section */}
      <div className="drjob-member-search">
        <form onSubmit={handleSearch}>
          <div className="drjob-search-wrapper">
            <label htmlFor="member-search">{t("members.search.label")}</label>
            <div className="drjob-search-input-group">
              <input
                type="text"
                id="member-search"
                className="drjob-search-input"
                placeholder={t("members.search.placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="submit" className="find-btn">
                {t("members.search.button")}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Members Table */}
      <div className="drjob-table-container member-list-wrapper">
        {loading ? (
          <div className="drjob-loading" style={{ textAlign: "center", padding: "20px" }}>
            {t("members.loading.members")}
          </div>
        ) : (
          <table className="drjob-member-table">
            <thead>
              <tr>
                <th>
                  {t("members.table.headers.name")}
                  {/* <i className="fa fa-sort"></i> */}
                </th>
                <th>{t("members.table.headers.email")}</th>
                <th>{t("members.table.headers.workspace")}</th>
                <th>{t("members.table.headers.dateCreated")}</th>
                <th>{t("members.table.headers.roleDesignation")}</th>
                <th>{t("members.table.headers.dateModified")}</th>
                <th>{t("members.table.headers.employeeCode")}</th>
                <th>{t("members.table.headers.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        {member.profile_photo_url ? (
                          <img
                            src={member.profile_photo_url}
                            alt={member.name}
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "50%",
                              backgroundColor: "#e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: "#666",
                            }}
                          >
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        {member.name}
                      </div>
                    </td>
                    <td>{member.email}</td>
                    <td>{member.assign_workspace}</td>
                    <td>{formatDate(member.created_at)}</td>
                    <td>{member.designation}</td>
                    <td>{formatUpdatedDate(member.updated_at)}</td>
                    <td>{member.employee_code}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn"
                          title={t("members.actions.resendWelcomeEmail")}
                          onClick={() => handleResendWelcomeEmailClick(member)}
                          disabled={isResendingEmail}
                        >
                          <i className="fa fa-link"></i>
                        </button>
                        {canEditMember && (
                          <Link to={URLS.MEMBERS.EDIT(member.user_id + "")} className="action-btn" title={t("members.actions.edit")}>
                            <i className="fa fa-edit"></i>
                          </Link>
                        )}
                        {canDeleteMember && (
                          <button
                            className="action-btn"
                            title={t("members.actions.delete")}
                            onClick={() => handleDeleteClick(member)}
                            disabled={isDeletingMember}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: "20px" }}>
                    {searchTerm ? t("members.table.noSearchResults") : t("members.table.noMembers")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {members.length > 0 && totalPages > 1 && (
        <div className="pagination">
          <button className="page-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            {t("members.pagination.prev")}
          </button>
          <div className="page-numbers">
            {/* Show first page if not in range */}
            {startPage > 1 && (
              <>
                <button className={`page-num ${currentPage === 1 ? "active" : ""}`} onClick={() => handlePageChange(1)}>
                  1
                </button>
                {startPage > 2 && <span>...</span>}
              </>
            )}

            {/* Page Numbers */}
            {pages.map((page) => (
              <button
                key={page}
                className={`page-num ${currentPage === page ? "active" : ""}`}
                onClick={() => handlePageChange(page)}
                disabled={loading}
              >
                {page}
              </button>
            ))}

            {/* Show last page if not in range */}
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <span>...</span>}
                <button
                  className={`page-num ${currentPage === totalPages ? "active" : ""}`}
                  onClick={() => handlePageChange(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>
          <button
            className="page-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            {t("members.pagination.next")}
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText={isDeletingMember ? t("members.loading.deleting") : t("members.modals.delete.confirmButton")}
          confirmBtnBsStyle="danger"
          cancelBtnText={t("members.modals.delete.cancelButton")}
          title={t("members.modals.delete.title")}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          disabled={isDeletingMember}
        >
          {t("members.modals.delete.message", { memberName: deleteModal.member?.name })}
        </SweetAlert>
      )}

      {/* Resend Welcome Email Confirmation Modal */}
      {resendModal.show && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText={isResendingEmail ? t("members.loading.sending") : t("members.modals.resendEmail.confirmButton")}
          confirmBtnBsStyle="preview"
          cancelBtnText={t("members.modals.resendEmail.cancelButton")}
          cancelBtnBsStyle="cancel"
          title={t("members.modals.resendEmail.title")}
          onConfirm={handleResendWelcomeEmailConfirm}
          onCancel={handleResendWelcomeEmailCancel}
          disabled={isResendingEmail}
        >
          {t("members.modals.resendEmail.message", { memberName: resendModal.member?.name })}
          <br />
          <br />
          <strong>{t("members.modals.resendEmail.note")}</strong>
        </SweetAlert>
      )}

      {/* Invite Member Modal */}
      <InviteMemberModal isOpen={inviteModalOpen} onClose={handleInviteClose} onSuccess={handleInviteSuccess} />
    </div>
  );
};

export default MemberList;
